/**
 * RouteBoardPanel — the right-hand panel of the route board.
 *
 * Lists each visible technician's route with a color chip; selecting a section
 * focuses that technician on the map. Each section expands to a drag-to-reorder
 * list of stops (one @dnd-kit SortableContext per route, so drags stay within a
 * single technician's route). Print actions are preserved per route and per stop.
 */
import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronDown,
  ChevronRight,
  GripVertical,
  Printer,
  User,
  Wrench,
  Wand2,
  Loader2,
  Route,
  Clock,
  RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { RouteStatus } from '@/types/technicianRoutes';
import type { BoardRoute } from '../utils/routeBoardMapping';
import type { RouteStop as MapRouteStop } from '../types';

/**
 * Re-optimize is temporarily disabled in the UI pending a rework of the
 * optimization flow (free-tier 11-stop cap / nearest-neighbor fallback). All
 * wiring is intact — flip this to `true` to restore the button.
 */
const RE_OPTIMIZE_ENABLED: boolean = false;

const statusColors: Record<RouteStatus, string> = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
};

/** Format minutes as "45 min" / "1h 20m" (matches RouteSummaryCard). */
const formatDuration = (minutes: number): string => {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
};

/** Format miles as "12.3 mi" (matches RouteSummaryCard). */
const formatDistance = (miles: number): string =>
  miles < 1 ? `${Math.round(miles * 5280)} ft` : `${miles.toFixed(1)} mi`;

interface RouteBoardPanelProps {
  routes: BoardRoute[];
  focusedTechnicianId: string | null;
  onFocusTechnician: (technicianId: string | null) => void;
  expandedRouteIds: Set<string>;
  onToggleExpanded: (routeId: string) => void;
  onReorderStops: (routeId: string, newStops: MapRouteStop[]) => void;
  onReoptimize: (routeId: string) => void;
  onRevertOptimized: (routeId: string) => void;
  onPrintRoute: (routeId: string) => void;
  onPrintDealer: (
    routeId: string,
    dealerId: string,
    stopNumber: number,
    totalStops: number
  ) => void;
  printStatus: Record<string, { isPrinted: boolean }>;
  reoptimizingRouteId?: string | null;
}

interface SortableStopRowProps {
  stop: MapRouteStop;
  index: number;
  total: number;
  color: string;
  isPrinted: boolean;
  onPrint: () => void;
}

const SortableStopRow: React.FC<SortableStopRowProps> = ({
  stop,
  index,
  total,
  color,
  isPrinted,
  onPrint
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stop.dealer.id
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.85 : 1
  };

  const isManual = stop.stop_type === 'manual';
  const recallCount = stop.recalls?.length ?? 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-sm',
        isDragging && 'shadow-lg ring-1 ring-primary/40',
        isPrinted && 'border-emerald-300 dark:border-emerald-700'
      )}
    >
      <button
        type='button'
        className='cursor-grab touch-none text-muted-foreground hover:text-foreground active:cursor-grabbing'
        aria-label={`Drag to reorder ${stop.dealer.name}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className='h-4 w-4' />
      </button>

      <span
        className='flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white'
        style={{ backgroundColor: color }}
      >
        {index + 1}
      </span>

      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-1.5'>
          <span className='truncate font-medium text-foreground'>{stop.dealer.name}</span>
          {isManual && <Wrench className='h-3 w-3 flex-shrink-0 text-sky-500' />}
        </div>
        <div className='truncate text-xs text-muted-foreground'>
          {stop.dealer.city}
          {stop.dealer.region ? `, ${stop.dealer.region}` : ''} · {recallCount} recall
          {recallCount !== 1 ? 's' : ''}
        </div>
      </div>

      <Button
        variant='ghost'
        size='sm'
        className='h-7 w-7 p-0 text-muted-foreground hover:text-foreground'
        title={`Print stop ${index + 1} of ${total}`}
        onClick={onPrint}
      >
        <Printer className='h-4 w-4' />
      </Button>
    </div>
  );
};

const RouteSection: React.FC<{
  route: BoardRoute;
  isFocused: boolean;
  isExpanded: boolean;
  isReoptimizing: boolean;
  onFocus: () => void;
  onToggleExpanded: () => void;
  onReorderStops: (newStops: MapRouteStop[]) => void;
  onReoptimize: () => void;
  onRevertOptimized: () => void;
  onPrintRoute: () => void;
  onPrintDealer: (dealerId: string, stopNumber: number, totalStops: number) => void;
  printStatus: Record<string, { isPrinted: boolean }>;
}> = ({
  route,
  isFocused,
  isExpanded,
  isReoptimizing,
  onFocus,
  onToggleExpanded,
  onReorderStops,
  onReoptimize,
  onRevertOptimized,
  onPrintRoute,
  onPrintDealer,
  printStatus
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = route.stops.findIndex(s => s.dealer.id === active.id);
    const newIndex = route.stops.findIndex(s => s.dealer.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(route.stops, oldIndex, newIndex).map((s, idx) => ({
      ...s,
      stop_order: idx + 1
    }));
    onReorderStops(reordered);
  };

  const totalStops = route.stops.length;

  return (
    <div
      className={cn(
        'rounded-lg border bg-card transition-colors',
        isFocused ? 'ring-2 ring-primary/50' : 'hover:bg-muted/30'
      )}
    >
      {/* Header */}
      <div
        className='flex cursor-pointer items-center gap-2 px-3 py-2'
        onClick={onFocus}
        role='button'
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onFocus();
          }
        }}
      >
        <button
          type='button'
          className='text-muted-foreground hover:text-foreground'
          aria-label={isExpanded ? 'Collapse stops' : 'Expand stops'}
          onClick={e => {
            e.stopPropagation();
            onToggleExpanded();
          }}
        >
          {isExpanded ? <ChevronDown className='h-4 w-4' /> : <ChevronRight className='h-4 w-4' />}
        </button>

        <span
          className='h-3 w-3 flex-shrink-0 rounded-full'
          style={{ backgroundColor: route.route_color }}
          aria-hidden
        />
        <User className='h-4 w-4 flex-shrink-0 text-muted-foreground' />
        <div className='min-w-0 flex-1'>
          <div className='flex items-center gap-1.5'>
            <span className='truncate font-medium text-foreground'>{route.technicianName}</span>
            {route.routeSequence > 1 && (
              <Badge variant='secondary' className='text-[10px]'>
                Route {route.routeSequence}
              </Badge>
            )}
          </div>
          {/* Stops · distance · estimated time (captured at assignment time) */}
          <div className='flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs text-muted-foreground'>
            <span>
              {totalStops} stop{totalStops !== 1 ? 's' : ''}
            </span>
            {route.total_distance > 0 && (
              <span className='flex items-center gap-0.5'>
                <Route className='h-3 w-3' />
                {formatDistance(route.total_distance)}
              </span>
            )}
            {route.total_duration > 0 && (
              <span className='flex items-center gap-0.5'>
                <Clock className='h-3 w-3' />
                {formatDuration(route.total_duration)}
              </span>
            )}
          </div>
        </div>

        <Badge className={cn('flex-shrink-0 text-[10px]', statusColors[route.status])}>
          {route.status.replace('_', ' ')}
        </Badge>
      </div>

      {/* Body */}
      {isExpanded && (
        <div className='space-y-2 border-t px-3 py-2'>
          <div className='flex items-center justify-between'>
            <span className='text-xs font-medium text-muted-foreground'>
              Drag stops to change the driving order
            </span>
            <div className='flex gap-1'>
              {RE_OPTIMIZE_ENABLED && (
                <Button
                  variant='ghost'
                  size='sm'
                  className='h-7 gap-1 px-2 text-xs'
                  onClick={onReoptimize}
                  disabled={isReoptimizing || totalStops < 2}
                  title='Re-run the automatic routing algorithm'
                >
                  {isReoptimizing ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  ) : (
                    <Wand2 className='h-3.5 w-3.5' />
                  )}
                  Re-optimize
                </Button>
              )}
              {route.algorithm === 'manual' && route.hasOriginalOptimized && (
                <Button
                  variant='ghost'
                  size='sm'
                  className='h-7 gap-1 px-2 text-xs'
                  onClick={onRevertOptimized}
                  disabled={isReoptimizing}
                  title='Restore the optimized stop order'
                >
                  <RotateCcw className='h-3.5 w-3.5' />
                  Revert
                </Button>
              )}
              <Button
                variant='outline'
                size='sm'
                className='h-7 gap-1 px-2 text-xs'
                onClick={onPrintRoute}
                title='Print this technician’s route sheet'
              >
                <Printer className='h-3.5 w-3.5' />
                Print
              </Button>
            </div>
          </div>

          {totalStops === 0 ? (
            <p className='py-2 text-center text-xs text-muted-foreground'>
              No stops on this route.
            </p>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={route.stops.map(s => s.dealer.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className='space-y-1.5'>
                  {route.stops.map((stop, index) => (
                    <SortableStopRow
                      key={stop.dealer.id}
                      stop={stop}
                      index={index}
                      total={totalStops}
                      color={route.route_color}
                      isPrinted={printStatus[stop.dealer.id]?.isPrinted ?? false}
                      onPrint={() => onPrintDealer(stop.dealer.id, index + 1, totalStops)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}
    </div>
  );
};

export const RouteBoardPanel: React.FC<RouteBoardPanelProps> = ({
  routes,
  focusedTechnicianId,
  onFocusTechnician,
  expandedRouteIds,
  onToggleExpanded,
  onReorderStops,
  onReoptimize,
  onRevertOptimized,
  onPrintRoute,
  onPrintDealer,
  printStatus,
  reoptimizingRouteId
}) => {
  return (
    <div className='space-y-2'>
      {routes.map(route => {
        const isFocused = route.technician_id === focusedTechnicianId;
        return (
          <RouteSection
            key={route.routeId}
            route={route}
            isFocused={isFocused}
            isExpanded={expandedRouteIds.has(route.routeId)}
            isReoptimizing={reoptimizingRouteId === route.routeId}
            onFocus={() => onFocusTechnician(isFocused ? null : route.technician_id)}
            onToggleExpanded={() => onToggleExpanded(route.routeId)}
            onReorderStops={newStops => onReorderStops(route.routeId, newStops)}
            onReoptimize={() => onReoptimize(route.routeId)}
            onRevertOptimized={() => onRevertOptimized(route.routeId)}
            onPrintRoute={() => onPrintRoute(route.routeId)}
            onPrintDealer={(dealerId, stopNumber, totalStops) =>
              onPrintDealer(route.routeId, dealerId, stopNumber, totalStops)
            }
            printStatus={printStatus}
          />
        );
      })}
    </div>
  );
};

export default RouteBoardPanel;
